import { BaseSectionPage } from "../components/base-page";
import {
  SummarySectionsSettings,
  defaultSettings,
} from "../summary-sections/settings";
import { compose } from "@macrostrat/hyper";
import { group } from "d3-array";

import { ColumnProvider, ColumnSVG } from "@macrostrat/column-components";
import { useContext } from "react";
import { SectionSurfacesProvider } from "~/sections/providers";
import { GeneralizedSectionsMap } from "../summary-sections/map";
import {
  SectionContainer,
  SectionLinkOverlay,
  SectionPositionProvider,
} from "../components";
import { SVGSectionInner } from "../summary-sections/column";
import { GeneralizedLithostratKey } from "../summary-sections/lithostrat-key";
import { GeneralizedAxis, GeneralizedBreaks } from "./axis";
import { GeneralizedChemostratigraphyColumn } from "./chemostrat";
import {
  ColumnDivisionsContext,
  GeneralizedDataProvider,
} from "./data-provider";
import { LegendInner } from "~/sections/summary-sections/legend";
import classNames from "classnames";

import { hyperStyled } from "@macrostrat/hyper";
import styles from "./main.module.styl";
const h = hyperStyled(styles);

export function GeneralizedSections(props) {
  return h(
    BaseSectionPage,
    {
      id: "generalized-sections-page",
      settingsPanel: SummarySectionsSettings,
      defaultSettings,
    },
    h(GeneralizedSectionsInner, { showChemostrat: true }),
  );
}

// Should allow switching between offset types
const stratOffsets = {
  Onis: 0,
  Ubisis: 300,
  Tsams: 200,
};

const compactOffsets = {
  Onis: 0,
  Ubisis: 270,
  Tsams: 0,
};

function SectionPane(props) {
  const { className, showChemostrat = true } = props;
  const { divisions } = useContext(ColumnDivisionsContext);
  const surfaceMap = group(divisions, (s) => s.section_id);
  const sections = Array.from(surfaceMap, function ([key, surfaces]) {
    surfaces.sort((a, b) => a.bottom - b.bottom);
    return { key, surfaces };
  });

  const order = ["Onis", "Ubisis", "Tsams"];
  sections.sort((a, b) => order.indexOf(a.key) - order.indexOf(b.key));

  const offsets = compactOffsets;

  const pixelsPerMeter = 12;

  return h(
    "div.section-pane",
    {
      className: classNames(className, {
        "with-chemostratigraphy": showChemostrat,
      }),
    },
    [
      h(LegendInner, { className: "legend" }),
      h(SectionContainer, [
        h(SectionLinkOverlay, {
          extendLinks: true,
          showSectionTrackers: false,
        }),
        h.if(showChemostrat)(GeneralizedChemostratigraphyColumn, {
          sections,
          simple: true,
          showLines: false,
          keySection: "Onis",
          pixelsPerMeter,
        }),
        h(GeneralizedLithostratKey, {
          zoom: 0.1,
          key: "key",
          keySection: "Onis",
          offset: 0,
          pixelsPerMeter,
        }),
        h(GeneralizedSectionsMap, {
          bounds: [16, -24.42, 16.28, -24.18],
          className: "inset-map",
          metersPerPixel: 140,
        }),
        h(
          "div.generalized-sections",
          sections.map(function ({ key, surfaces }) {
            let start = 0;
            // Bottom is the first division with an assigned facies
            for (let d of Array.from(surfaces)) {
              if (d.facies != null && d.facies !== "none") {
                start = d.bottom;
                break;
              }
            }
            // Top of the last section is taken as the height
            // at which to clip off errant facies
            const end = parseFloat(surfaces[surfaces.length - 1].top);

            return h(GeneralizedSection, {
              id: key,
              zoom: 1,
              key,
              triangleBarRightSide: key === "Onis",
              offsetTop: offsets[key],
              start,
              end,
              range: [start, end],
              height: end - start,
              divisions: surfaces,
              pixelsPerMeter,
              sequenceStratOrder: [1, 1],
            });
          }),
        ),
      ]),
    ],
  );
}

function GeneralizedSection(props) {
  const { range, height, divisions, zoom, offsetTop, pixelsPerMeter, ...rest } =
    props;
  const { id } = rest;
  return h("div.section-column", { className: id }, [
    h(
      ColumnProvider,
      {
        range,
        height,
        divisions,
        zoom: 0.1,
        pixelsPerMeter,
      },
      [
        h(
          SVGSectionInner,
          {
            ...rest,
            offsetTop,
            absolutePosition: false,
            axisComponent: GeneralizedAxis,
            trackerOffset: { x: 65, y: 0 },
          },
          [
            h(GeneralizedBreaks),
            //h(SectionFigureReferences)
          ],
        ),
      ],
    ),
  ]);
}

const ProviderStack = compose(
  SectionSurfacesProvider,
  GeneralizedDataProvider,
  SectionPositionProvider,
);

export function GeneralizedSectionsInner(props) {
  const { showChemostrat = true } = props;
  return h(ProviderStack, h(SectionPane, { showChemostrat }));
}
